/**
 * The canvas, and everything about looking at it.
 *
 * Zoom, pan, fit, and hit-testing. What is drawn lives in LayerNode; what is
 * drawn *on top* - marks, selection - lives here, because it belongs to the
 * view rather than to the document.
 *
 * One rule holds the whole thing together: the Konva stage is positioned in
 * screen pixels and the page's own origin is a Group at (view.x, view.y). Every
 * layer inside it is placed with mmToPx(mm, zoom). Nothing else scales, so a
 * hairline stays a hairline and a 3 mm safe margin is 3 mm at every zoom.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Group, Layer as KonvaLayer, Path, Rect, Stage } from 'react-konva';
import type Konva from 'konva';
import { useEditor } from '../state/store';
import { mmToPx, pxToMm, toCss } from '../model/units';
import { PrintMarks } from './PrintMarks';
import { RulerGuides } from './RulerGuides';
import { LayerNode } from './LayerNode';
import { SelectionBox } from './SelectionBox';
import { CropBox } from './CropBox';
import { NodeEditor } from './NodeEditor';
import { PenTool } from './PenTool';
import { PencilTool } from './PencilTool';
import { Guides } from './Guides';
import { TextEditor } from './TextEditor';
import { Grid, Rulers } from './Rulers';
import type { Colour, Page } from '../model/types';
import { FIT_BOTTOM, FIT_TOP, fromActive } from './stack';
import { PageHeads } from './PageHeads';
import { SelectionMenu } from './SelectionMenu';
import { cornerRadius, findLayer, foldsOf } from '../model/types';
import { getConfig } from '../api';
import { inkAt } from '../colour/picking';
import { fromHex } from '../ui/ColourField';

/** How much room to leave either side of the page when fitting. */
const FIT_PADDING = 96;

export const MIN_ZOOM = 0.05;
export const MAX_ZOOM = 32;

interface CanvasProps {
  /** Hands the fit function up, so the toolbar and Ctrl+0 can call it. */
  onFitReady?: (fit: () => void) => void;
  /** Hands up a way to photograph the artwork, for the basket thumbnail. */
  onThumbReady?: (take: () => string | null) => void;
  /** Where to open the layer menu, or null to close it. */
  onMenu?: (at: { x: number; y: number } | null) => void;
  /** A drawing tool is out, so the canvas is being drawn on rather than selected in. */
  drawing?: 'pen' | 'pencil' | null;
  onDrawn?: () => void;
  /** Anything that floats over the canvas - the page and zoom strip. */
  children?: ReactNode;
}

export function CanvasView({ onFitReady, onThumbReady, onMenu, drawing, onDrawn, children }: CanvasProps) {
  const host = useRef<HTMLDivElement>(null);
  const stage = useRef<Konva.Stage>(null);
  const artwork = useRef<Konva.Layer>(null);

  const doc = useEditor((s) => s.doc);
  const pageIndex = useEditor((s) => s.pageIndex);
  const view = useEditor((s) => s.view);
  const setView = useEditor((s) => s.setView);
  const select = useEditor((s) => s.select);
  const toggleSelect = useEditor((s) => s.toggleSelect);

  const sampling = useEditor((s) => s.sampling);

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [spaceHeld, setSpaceHeld] = useState(false);

  /** The rubber band, in page millimetres, while one is being dragged. */
  const [band, setBand] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const bandFrom = useRef<{ x: number; y: number } | null>(null);

  const page = doc?.pages[pageIndex] ?? null;

  /* The stage fills its box, and the box is whatever the grid gives it. */
  useLayoutEffect(() => {
    const element = host.current;

    if (!element) return;

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;

      const { width, height } = entry.contentRect;

      setSize({ width: Math.round(width), height: Math.round(height) });
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  /**
   * Fits the active page, and picks a zoom that leaves a margin around it.
   *
   * Room is left above for the floating toolbar and the page's own title, and
   * below for the bottom bar, so none of them sits on the artwork. When there
   * is more than one page the page goes to the top rather than the middle, so
   * the next one shows beneath it and the customer can see there is more.
   */
  const fit = useCallback(() => {
    if (!page || !doc || size.width === 0 || size.height === 0) return;

    const bleed = doc.bleed;
    const sheetW = mmToPx(page.size.w + bleed * 2);
    const sheetH = mmToPx(page.size.h + bleed * 2);

    const room = size.height - FIT_TOP - FIT_BOTTOM;
    const zoom = Math.min((size.width - FIT_PADDING * 2) / sheetW, room / sheetH);
    const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));
    const height = mmToPx(page.size.h, clamped);
    const bleedPx = mmToPx(bleed, clamped);

    setView({
      zoom: clamped,
      x: (size.width - mmToPx(page.size.w, clamped)) / 2,
      y: doc.pages.length > 1 ? FIT_TOP + bleedPx : FIT_TOP + Math.max(bleedPx, (room - height) / 2),
    });
  }, [page, doc, size.width, size.height, setView]);

  // Fit on opening and when the window changes shape - not on every zoom, or
  // the customer could never zoom in, and not on changing page: the pages are
  // stacked, and changing page is moving between them, not a new view.
  const designId = useEditor((s) => s.design?.uuid);

  /**
   * The zoom the last fit chose, and the box it chose it for.
   *
   * What tells a customer's zoom apart from ours. Opening the 3D preview halves
   * the canvas, and refitting on that would throw away a view somebody had
   * zoomed to 400% to place a logo in - they pressed a preview button, not
   * "start again". So: still at our zoom, refit; zoomed themselves, keep it and
   * simply hold the same artwork under their eye as the box changes width.
   */
  const fitted = useRef<{ zoom: number; width: number } | null>(null);

  useEffect(() => {
    if (size.width === 0 || size.height === 0) return;

    const view = useEditor.getState().view;
    const theirs = fitted.current && Math.abs(view.zoom - fitted.current.zoom) > 0.001;

    if (theirs && fitted.current) {
      // Half of what the box gained or lost, so the point in the middle of the
      // canvas stays in the middle of it.
      const moved = (size.width - fitted.current.width) / 2;

      fitted.current = { zoom: view.zoom, width: size.width };

      if (moved) setView({ x: view.x + moved });

      return;
    }

    fit();

    fitted.current = { zoom: useEditor.getState().view.zoom, width: size.width };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designId, size.width, size.height]);

  /**
   * Makes another page the active one, then selects whatever was under the
   * pointer on it - so a click on an object on the back selects it, in one
   * click, as it would in Canva. The page's objects only become clickable once
   * it is active and drawn as such, hence waiting two frames to look.
   */
  const activate = useCallback((index: number) => {
    const pointer = stage.current?.getPointerPosition();

    useEditor.getState().goToPage(index);

    if (!pointer) return;

    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const shape = artwork.current?.getIntersection(pointer);
        const id = shape ? layerUnder(shape) : '';

        if (id) useEditor.getState().select([id]);
      })
    );
  }, []);

  useEffect(() => {
    onFitReady?.(fit);
  }, [onFitReady, fit]);

  /**
   * Hands up a way to take a picture of the artwork alone.
   *
   * At the trim size, so the thumbnail is the card rather than the card plus
   * the bleed a customer never sees, and at a pixel ratio that gives something
   * legible in a basket without sending a megabyte of it.
   */
  useEffect(() => {
    if (!onThumbReady) return;

    onThumbReady(() => {
      const layer = artwork.current;
      const current = useEditor.getState();
      const which = current.doc?.pages[current.pageIndex];

      if (!layer || !which) return null;

      try {
        const width = mmToPx(which.size.w, current.view.zoom);
        const height = mmToPx(which.size.h, current.view.zoom);
        const ratio = Math.min(2, 480 / Math.max(1, width));
        const radius = current.doc ? cornerRadius(current.doc, which.size) : 0;

        if (!radius && !which.cut) {
          return layer.toDataURL({ x: current.view.x, y: current.view.y, width, height, pixelRatio: ratio, mimeType: 'image/png' });
        }

        /*
         * A card with rounded corners is pictured with them. The layer is read
         * into a canvas and drawn again through the rounded outline, so the
         * basket shows the card as it will come off the cutter - square
         * corners there would be the one thing on the thumbnail that is wrong.
         * A shaped page (a tapered mug's fan) is pictured cut to its shape.
         */
        const shot = layer.toCanvas({ x: current.view.x, y: current.view.y, width, height, pixelRatio: ratio });
        const out = document.createElement('canvas');

        out.width = shot.width;
        out.height = shot.height;

        const ctx = out.getContext('2d');

        if (!ctx) return shot.toDataURL('image/png');

        if (which.cut) {
          ctx.scale(out.width / which.size.w, out.height / which.size.h);
          ctx.clip(new Path2D(which.cut));
          ctx.setTransform(1, 0, 0, 1, 0, 0);
        } else {
          ctx.beginPath();
          roundedPath(ctx, out.width, out.height, mmToPx(radius, current.view.zoom) * ratio);
          ctx.clip();
        }
        ctx.drawImage(shot, 0, 0);

        return out.toDataURL('image/png');
      } catch {
        // A canvas tainted by an image we could not read cross-origin. The
        // design is fine; it simply has no picture of itself.
        return null;
      }
    });
  }, [onThumbReady]);

  /* Space to pan, as every design tool does. */
  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      if (event.code === 'Space' && !isTyping(event.target)) {
        event.preventDefault();
        setSpaceHeld(true);
      }
    };

    const up = (event: KeyboardEvent) => {
      if (event.code === 'Space') setSpaceHeld(false);
    };

    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);

    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  /**
   * Wheel zooms about the pointer; trackpad two-finger scroll pans.
   *
   * The distinction is ctrlKey, which browsers set for a pinch gesture on a
   * trackpad. Getting this wrong makes a Mac feel broken.
   */
  const onWheel = useCallback(
    (event: Konva.KonvaEventObject<WheelEvent>) => {
      event.evt.preventDefault();

      const node = stage.current;

      if (!node) return;

      const pointer = node.getPointerPosition();

      if (!pointer) return;

      if (event.evt.ctrlKey || event.evt.metaKey) {
        const factor = Math.exp(-event.evt.deltaY / 240);
        const zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, view.zoom * factor));

        // Keep whatever is under the pointer under the pointer.
        const ratio = zoom / view.zoom;

        setView({
          zoom,
          x: pointer.x - (pointer.x - view.x) * ratio,
          y: pointer.y - (pointer.y - view.y) * ratio,
        });

        return;
      }

      const next = { x: view.x - event.evt.deltaX, y: view.y - event.evt.deltaY };

      setView(next);

      /*
       * The page scrolled to becomes the one being worked on, as in Canva - so
       * the toolbar and the page count describe the page on screen. Only with
       * nothing selected: scrolling must never drop what the customer chose.
       */
      const store = useEditor.getState();

      if (!doc || doc.pages.length < 2 || store.selection.length > 0 || store.editing || store.cropping || store.shaping) {
        return;
      }

      const middle = size.height / 2;
      let nearest = store.pageIndex;
      let distance = Infinity;

      doc.pages.forEach((each, index) => {
        const at = fromActive(doc, index, store.pageIndex, view.zoom);
        const centre = next.y + at.y + mmToPx(each.size.h / 2, view.zoom);
        const off = Math.abs(centre - middle);

        if (off < distance) {
          distance = off;
          nearest = index;
        }
      });

      if (nearest !== store.pageIndex) store.goToPage(nearest);
    },
    [view, setView, doc, size.height]
  );

  /**
   * Where the pointer is on the page, in millimetres.
   *
   * The stage is not transformed - the page sits in a Group at (view.x, view.y)
   * - so the pointer has to be brought into page space by hand.
   */
  const pageAt = useCallback(() => {
    const pointer = stage.current?.getPointerPosition();

    if (!pointer) return null;

    return {
      x: pxToMm(pointer.x - view.x, view.zoom),
      y: pxToMm(pointer.y - view.y, view.zoom),
    };
  }, [view.x, view.y, view.zoom]);

  /**
   * Starts a rubber band, but only on empty canvas.
   *
   * Pressing on an object is that object's drag, and pressing with space held
   * is a pan. Both have to be let through untouched, or the band steals a
   * gesture the customer meant for something else.
   */
  /*
   * The eyedropper.
   *
   * While a colour control is waiting for one, a sheet over the canvas takes
   * the click, and the colour comes from the document rather than the screen -
   * see colour/picking.ts for why that matters. A photograph has no ink of its
   * own, so for one the pixel is read and converted, which is the best there is.
   *
   * Escape puts the dropper down without closing the colour control that asked
   * for it, which is why it is caught here first, in the capture phase.
   */
  useEffect(() => {
    if (!sampling) return;

    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      useEditor.getState().endSampling();
    };

    window.addEventListener('keydown', key, true);

    return () => window.removeEventListener('keydown', key, true);
  }, [sampling]);

  const onSample = useCallback((event: Konva.KonvaEventObject<MouseEvent>) => {
    // Kept from the stage's own click, which would otherwise read this as a
    // click on bare paper and deselect the object whose colour is being set.
    event.cancelBubble = true;

    const store = useEditor.getState();
    const to = store.sampling;
    const konva = stage.current;
    const art = artwork.current;
    const pageNow = store.page();
    const pointer = konva?.getPointerPosition();

    store.endSampling();

    if (!to || !art || !pageNow || !pointer) return;

    const at = {
      x: pxToMm(pointer.x - store.view.x, store.view.zoom),
      y: pxToMm(pointer.y - store.view.y, store.view.zoom),
    };

    const shape = art.getIntersection(pointer);
    const id = shape ? layerUnder(shape) : '';
    const layer = id ? findLayer(pageNow.layers, id) : null;

    let colour = layer ? inkAt(layer, at) : null;

    // Bare paper with a colour on it: that colour, exactly.
    if (!colour && !layer && pageNow.background?.type === 'colour') {
      const bleed = store.doc?.bleed ?? 0;
      const onSheet = at.x >= -bleed && at.y >= -bleed && at.x <= pageNow.size.w + bleed && at.y <= pageNow.size.h + bleed;

      if (onSheet) colour = pageNow.background.value;
    }

    if (!colour) colour = pixelAt(art, pointer);

    if (colour) to(colour);
  }, []);

  const onMouseDown = useCallback(
    (event: Konva.KonvaEventObject<MouseEvent>) => {
      if (spaceHeld || event.evt.button !== 0) return;

      // The eyedropper's click is its own; it starts no rubber band. Nor does
      // a drawing tool's: dragging out a smooth point with the pen used to
      // drag a selection band behind it, and select whatever the band touched
      // on letting go.
      if (useEditor.getState().sampling || drawing) return;

      // Pressing on an object picks it up; the band is for pressing on paper.
      // Asking the shape for its name got an empty string, so every press on an
      // object started a rubber band - and the band then swallowed the click
      // that would have selected it.
      if (event.target !== event.target.getStage() && layerUnder(event.target)) return;

      const at = pageAt();

      if (!at) return;

      bandFrom.current = at;
      setBand({ x1: at.x, y1: at.y, x2: at.x, y2: at.y });
    },
    [spaceHeld, pageAt, drawing]
  );

  /**
   * Right-click opens the layer menu on whatever is under the pointer.
   *
   * Clicking something outside the selection selects it first, which is what
   * every editor does and what stops a customer acting on a layer they cannot
   * see is chosen. Right-clicking bare paper selects nothing and shows no menu.
   */
  const onRightClick = useCallback(
    (event: Konva.KonvaEventObject<PointerEvent>) => {
      // Konva has no menu of its own, and the browser's is no use here.
      event.evt.preventDefault();

      const store = useEditor.getState();

      // The right button puts the eyedropper down, as it does most tools.
      if (store.sampling) {
        store.endSampling();

        return;
      }
      const name = layerUnder(event.target);

      if (!name) {
        store.select([]);
        onMenu?.(null);

        return;
      }

      if (!store.selection.includes(name)) store.select([name]);

      onMenu?.({ x: event.evt.clientX, y: event.evt.clientY });
    },
    [onMenu]
  );

  const onMouseMove = useCallback(() => {
    const from = bandFrom.current;

    if (!from) return;

    const at = pageAt();

    if (at) setBand({ x1: from.x, y1: from.y, x2: at.x, y2: at.y });
  }, [pageAt]);

  const onMouseUp = useCallback(
    (event: Konva.KonvaEventObject<MouseEvent>) => {
      const box = band;

      bandFrom.current = null;
      setBand(null);

      if (!box || !page) return;

      const left = Math.min(box.x1, box.x2);
      const top = Math.min(box.y1, box.y2);
      const right = Math.max(box.x1, box.x2);
      const bottom = Math.max(box.y1, box.y2);

      /*
       * A band smaller than this was a click, not a drag.
       *
       * It is still a click on bare paper, though - a band only ever starts
       * there - so it clears the selection, which is what a customer means by
       * clicking away from everything. The click event itself cannot do it:
       * it sees a band was in progress and stands aside, and by then this has
       * already run.
       */
      if (right - left < 0.5 && bottom - top < 0.5) {
        if (!event.evt.shiftKey) select([]);

        return;
      }

      // Touching, not enclosing. Requiring a customer to surround an object
      // entirely means they cannot band-select anything that runs off the page,
      // which on a bleed design is most of the background.
      const caught = page.layers
        .filter(
          (layer) =>
            !layer.locked &&
            !layer.hidden &&
            layer.x < right &&
            layer.x + layer.w > left &&
            layer.y < bottom &&
            layer.y + layer.h > top
        )
        .map((layer) => layer.id);

      select(event.evt.shiftKey ? Array.from(new Set(useEditor.getState().selection.concat(caught))) : caught);
    },
    [band, page, select]
  );

  /** Clicking the surround, rather than an object, clears the selection. */
  const onStageClick = useCallback(
    (event: Konva.KonvaEventObject<MouseEvent>) => {
      // A click that ended a rubber band has already decided the selection.
      if (spaceHeld || band) return;

      // A click that finished a drawing has too: the new drawing is selected,
      // and this same click, landing on the tool's sheet, used to read as a
      // click on bare paper and deselect it again.
      if (drawing) return;

      /*
       * Left button only.
       *
       * Konva raises "click" for whichever button went down and came up on the
       * same shape, right one included - so opening the menu also reselected
       * whatever was under the pointer, and right-clicking one of several
       * chosen objects silently dropped the rest. Which is exactly when a
       * customer is reaching for Group.
       */
      if (event.evt.button !== 0) return;

      const name = layerUnder(event.target);
      const onPage = event.target !== event.target.getStage();

      if (!name || !onPage) {
        select([]);

        return;
      }

      if (event.evt.shiftKey) toggleSelect(name);
      else select([name]);
    },
    [spaceHeld, band, select, toggleSelect, drawing]
  );

  const onDragEnd = useCallback(
    (event: Konva.KonvaEventObject<DragEvent>) => {
      if (event.target !== event.target.getStage()) return;

      setView({ x: event.target.x(), y: event.target.y() });
    },
    [setView]
  );

  if (!doc || !page) {
    return (
      <div className="tb-canvas" ref={host}>
        <p className="tb-canvas__waiting">Opening your design…</p>
        {children}
      </div>
    );
  }

  const zoom = view.zoom;

  return (
    <div
      className={`tb-canvas${spaceHeld ? ' tb-canvas--panning' : ''}${sampling ? ' tb-canvas--sampling' : ''}`}
      ref={host}
    >
      <Grid />

      <Stage
        ref={stage}
        className="tb-canvas__stage"
        width={size.width || 1}
        height={size.height || 1}
        x={0}
        y={0}
        draggable={spaceHeld}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onClick={onStageClick}
        onTap={onStageClick}
        onContextMenu={onRightClick}
        onDragEnd={onDragEnd}
      >
        {/*
          Two layers, and the division is exactly "would this be printed".
          Artwork on one, marks and handles on the other - which is what lets a
          thumbnail be exported without hiding anything first, and would
          otherwise have meant flicking the whole editor into preview mode for
          a frame every time a design was saved.
        */}
        <KonvaLayer ref={artwork}>
          {/*
            Every page, stacked (stack.ts). The active one is drawn at the view's
            origin and is the one being edited; the others are drawn at their
            distance from it, and a click on one makes it the active page.
          */}
          {doc.pages.map((each, index) => {
            const at = fromActive(doc, index, pageIndex, zoom);

            return (
              <Sheet
                key={each.id}
                page={each}
                radius={cornerRadius(doc, each.size)}
                bleed={doc.bleed}
                zoom={zoom}
                preview={view.preview}
                x={view.x + at.x}
                y={view.y + at.y}
                onActivate={index === pageIndex ? undefined : () => activate(index)}
              />
            );
          })}
        </KonvaLayer>

        <KonvaLayer>
          {/* The other pages' marks, so every sheet shows its own trim and bleed. */}
          {doc.pages.map((each, index) => {
            if (index === pageIndex) return null;

            const at = fromActive(doc, index, pageIndex, zoom);

            return (
              <Group key={each.id} x={view.x + at.x} y={view.y + at.y} listening={false}>
                <PrintMarks
                  width={each.size.w}
                  height={each.size.h}
                  radius={cornerRadius(doc, each.size)}
                  cut={each.cut}
                  round={each.round}
                  folds={foldsOf(doc, each.size)}
                  spread={each.spread}
                  bleed={doc.bleed}
                  safe={doc.safe}
                  zoom={zoom}
                  preview={view.preview}
                  labels={false}
                  shade={each.shade}
                  eyelets={each.eyelets}
                />
              </Group>
            );
          })}

          <Group x={view.x} y={view.y}>
            <PrintMarks
              width={page.size.w}
              height={page.size.h}
              radius={cornerRadius(doc, page.size)}
              cut={page.cut}
              round={page.round}
              folds={foldsOf(doc, page.size)}
              panels={getConfig().folds?.names}
              spread={page.spread}
              bleed={doc.bleed}
              safe={doc.safe}
              zoom={zoom}
              preview={view.preview}
              shade={page.shade}
              eyelets={page.eyelets}
            />

            <RulerGuides zoom={zoom} />

            {!drawing && <SelectionBox zoom={zoom} />}
            <CropBox zoom={zoom} />
            <NodeEditor zoom={zoom} />
            <Guides zoom={zoom} />

            {band && (
              <Rect
                x={mmToPx(Math.min(band.x1, band.x2), zoom)}
                y={mmToPx(Math.min(band.y1, band.y2), zoom)}
                width={mmToPx(Math.abs(band.x2 - band.x1), zoom)}
                height={mmToPx(Math.abs(band.y2 - band.y1), zoom)}
                fill="rgba(221, 29, 38, 0.08)"
                stroke="#dd1d26"
                strokeWidth={1}
                strokeScaleEnabled={false}
                listening={false}
              />
            )}
          </Group>

          {/*
            The pen sits outside the panned group and works in stage
            coordinates, because it is placing points on the page rather than
            drawing something that belongs to one.
          */}
          {drawing === 'pen' && <PenTool zoom={zoom} onDone={() => onDrawn?.()} />}
          {drawing === 'pencil' && <PencilTool zoom={zoom} onDone={() => onDrawn?.()} />}

          {/* Over everything, handles included: while the dropper is out, a
              click anywhere is a question about colour. */}
          {sampling && (
            <Rect id="tb-eyedropper" x={-1e5} y={-1e5} width={2e5} height={2e5} onClick={onSample} onTap={onSample} />
          )}
        </KonvaLayer>
      </Stage>

      <PageHeads />
      <SelectionMenu stage={stage} onMenu={(at) => onMenu?.(at)} hidden={!!drawing} />
      <TextEditor />
      <Rulers />

      {children}
    </div>
  );
}

/** Where the pointer is on the page, in millimetres. For rulers and snapping. */
export function pointerMm(stage: Konva.Stage, view: { x: number; y: number; zoom: number }) {
  const pointer = stage.getPointerPosition();

  if (!pointer) return null;

  return {
    x: pxToMm(pointer.x - view.x, view.zoom),
    y: pxToMm(pointer.y - view.y, view.zoom),
  };
}

/** Whether a key event came from somewhere a shortcut must not steal from. */
/**
 * Which layer an event happened in.
 *
 * Konva reports the shape that was hit, and the shape is not what carries the
 * layer's id - the group around it is, because a layer can be several shapes
 * and dragging has to move all of them. Asking the shape for a name gets an
 * empty string, which read as "the customer clicked bare paper".
 *
 * The effect was quiet: dragging an object selects it on the way, so a click
 * that failed to select was almost always followed by one that did. Clicking
 * once, precisely, on a shape and having nothing happen was the visible part.
 */
/**
 * One page: its paper and its artwork.
 *
 * The paper, while laying out, is the whole sheet including the part that gets
 * cut off, because the customer has to see their artwork running past the
 * trim. In preview it is the finished card and nothing else - the bleed is cut
 * off rather than merely unmarked, since a band running past the edge of a card
 * that has no edge yet is exactly what they most need to check.
 *
 * A page that is not the active one is drawn but not handled: its objects do
 * not drag or select, and a click anywhere on it makes it the active page -
 * then selects what was under the pointer, so it takes one click, as in Canva.
 */
function Sheet({
  page,
  radius: radiusMm = 0,
  bleed: bleedMm,
  zoom,
  preview,
  x,
  y,
  onActivate,
}: {
  page: Page;
  /** Rounded corners, in mm; 0 for square. Only the preview is cut to them. */
  radius?: number;
  bleed: number;
  zoom: number;
  preview: boolean;
  x: number;
  y: number;
  onActivate?: () => void;
}) {
  const bleed = mmToPx(bleedMm, zoom);
  const sheetW = mmToPx(page.size.w, zoom);
  const sheetH = mmToPx(page.size.h, zoom);
  const background = page.background?.type === 'colour' ? toCss(page.background.value) : '#ffffff';
  const inert = !!onActivate;
  const radius = mmToPx(radiusMm, zoom);

  /*
   * In preview the card is cut, and cut to whatever shape it really is.
   *
   * Three cases, in order of how particular they are: a die line the product
   * gave it - a medal insert's circle, a mug's fan - then rounded corners, then
   * a plain rectangle. The shaped one used to be missing, so a round medal
   * previewed as a white square: the trim was drawn round by PrintMarks, but
   * preview hides those marks, and the paper underneath had never been told.
   */
  const shaped = preview && !!page.cut;
  const rounded = preview && !shaped && radius > 0;

  // The cut is written in page millimetres; the sheet is drawn in pixels.
  const scaleX = page.size.w > 0 ? sheetW / page.size.w : 1;
  const scaleY = page.size.h > 0 ? sheetH / page.size.h : 1;

  return (
    <Group x={x} y={y}>
      {shaped ? (
        <Path
          data={page.cut}
          scaleX={scaleX}
          scaleY={scaleY}
          fill={background}
          shadowColor="rgba(0,0,0,0.25)"
          shadowBlur={16}
          shadowOffsetY={3}
          shadowOpacity={0.5}
          listening={false}
        />
      ) : (
        <Rect
          x={preview ? 0 : -bleed}
          y={preview ? 0 : -bleed}
          width={preview ? sheetW : sheetW + bleed * 2}
          height={preview ? sheetH : sheetH + bleed * 2}
          cornerRadius={rounded ? radius : 0}
          fill={background}
          shadowColor="rgba(0,0,0,0.25)"
          shadowBlur={16}
          shadowOffsetY={3}
          shadowOpacity={0.5}
          listening={false}
        />
      )}

      <Group
        clipX={preview && !rounded && !shaped ? 0 : undefined}
        clipY={preview && !rounded && !shaped ? 0 : undefined}
        clipWidth={preview && !rounded && !shaped ? sheetW : undefined}
        clipHeight={preview && !rounded && !shaped ? sheetH : undefined}
        clipFunc={
          shaped
            ? (ctx) => {
                ctx.beginPath();
                cutPath(ctx, page.cut ?? '', scaleX, scaleY);
                ctx.closePath();
              }
            : rounded
              ? (ctx) => {
                  ctx.beginPath();
                  roundedPath(ctx, sheetW, sheetH, radius);
                  ctx.closePath();
                }
              : undefined
        }
        listening={!inert}
      >
        {page.layers.map((layer) => (
          <LayerNode key={layer.id} layer={layer} zoom={zoom} />
        ))}
      </Group>

      {inert && (
        <Rect
          id={`tb-page-${page.id}`}
          x={-bleed}
          y={-bleed}
          width={sheetW + bleed * 2}
          height={sheetH + bleed * 2}
          // Kept from the stage's own handlers: this press starts no band and
          // this click does not deselect.
          onMouseDown={(event) => {
            event.cancelBubble = true;
          }}
          onClick={(event) => {
            event.cancelBubble = true;
            onActivate?.();
          }}
          onTap={(event) => {
            event.cancelBubble = true;
            onActivate?.();
          }}
        />
      )}
    </Group>
  );
}

/**
 * The colour of the screen at a point, as ink. For photographs, which have no
 * ink of their own to report. Transparent means nothing was there to pick.
 */
function pixelAt(layer: Konva.Layer, pointer: { x: number; y: number }): Colour | null {
  const canvas = layer.getCanvas();
  const ratio = canvas.getPixelRatio();
  const data = canvas.getContext().getImageData(Math.round(pointer.x * ratio), Math.round(pointer.y * ratio), 1, 1).data;

  if ((data[3] ?? 0) < 128) return null;

  const hex = `#${[data[0], data[1], data[2]].map((v) => (v ?? 0).toString(16).padStart(2, '0')).join('')}`;
  const ink = fromHex(hex);

  return ink ? { space: 'cmyk', value: ink } : null;
}

function layerUnder(target: Konva.Node): string {
  let node: Konva.Node | null = target;
  const stage = target.getStage();
  let found = '';

  /*
   * The outermost name, not the first one up. A group's children are named
   * too - the arrange tests measure one by name, and findOne needs them - but
   * what a customer clicked is the group: selection holds the page's own
   * layers, and a child of a group is not one of them. Stopping at the first
   * name would select something that cannot be selected.
   */
  while (node && node !== stage) {
    const name = node.name();

    if (name) found = name;

    node = node.getParent();
  }

  return found;
}

export function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;

  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  );
}

/**
 * A rectangle with rounded corners, from the origin, into a path being built.
 *
 * By arcs rather than roundRect(), which Konva's own context does not always
 * pass through and older browsers do not have.
 */
function roundedPath(
  ctx: { moveTo: (x: number, y: number) => void; arcTo: (x1: number, y1: number, x2: number, y2: number, r: number) => void; lineTo: (x: number, y: number) => void },
  w: number,
  h: number,
  radius: number
) {
  const r = Math.max(0, Math.min(radius, w / 2, h / 2));

  ctx.moveTo(r, 0);
  ctx.lineTo(w - r, 0);
  ctx.arcTo(w, 0, w, r, r);
  ctx.lineTo(w, h - r);
  ctx.arcTo(w, h, w - r, h, r);
  ctx.lineTo(r, h);
  ctx.arcTo(0, h, 0, h - r, r);
  ctx.lineTo(0, r);
  ctx.arcTo(0, 0, r, 0, r);
}

/**
 * A page's die line, in millimetres, into a path being built in pixels.
 *
 * Moves, lines, cubics and close - which is the whole of it: the document
 * sanitiser accepts no other command in a cut, so there is no case here that a
 * saved design can contain and this cannot draw.
 *
 * The scale is passed rather than applied to the context, because the context
 * being built is shared with the shapes drawn after it and a transform left on
 * it would move them too.
 */
function cutPath(
  ctx: {
    moveTo: (x: number, y: number) => void;
    lineTo: (x: number, y: number) => void;
    bezierCurveTo: (a: number, b: number, c: number, d: number, e: number, f: number) => void;
    closePath: () => void;
  },
  data: string,
  scaleX: number,
  scaleY: number
) {
  // 'M 25 0 C 38.8 0 50 11.2 50 25 ... Z' - one letter, then its numbers.
  const parts = data.match(/[MLCZ]|-?\d*\.?\d+/gi);

  if (!parts) return;

  let at = 0;

  const next = () => Number(parts[at++] ?? 0);

  while (at < parts.length) {
    switch ((parts[at++] ?? '').toUpperCase()) {
      case 'M':
        ctx.moveTo(next() * scaleX, next() * scaleY);
        break;
      case 'L':
        ctx.lineTo(next() * scaleX, next() * scaleY);
        break;
      case 'C':
        ctx.bezierCurveTo(
          next() * scaleX,
          next() * scaleY,
          next() * scaleX,
          next() * scaleY,
          next() * scaleX,
          next() * scaleY
        );
        break;
      case 'Z':
        ctx.closePath();
        break;
      default:
        // A stray number outside any command: nothing sane to do with it.
        break;
    }
  }
}
